import { replaceEqualDeep } from '@zg-rentals/react-query5';
import { httpFrontEndRequest } from '@zg-rentals/ts-client-http-client';
import { useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import type { QueryClient, QueryKey } from '@zg-rentals/react-query5';
import type { AccountViewListingAdvertisingDto } from '@zg-rentals/ts-rental-manager-api-client';

// Written as a direct read because the generated client does not carry advertisingPage yet. Once it does, the
// request below should collapse into the generated call.
export const SINGLE_FAMILY_ADVERTISING_PAGE_PATH =
  '/api/web/v2/users/accountView/zram/singleFamily/properties/advertisingPage';

// The list page size, so one list page is one read.
const ADVERTISING_PAGE_SIZE = PropertiesConstants.PROPERTIES_LIMIT_DEFAULT;

type AdvertisingPageParams = { listingAliases: string[]; bypassCache?: boolean };
type AdvertisingByListingAlias = Record<string, AccountViewListingAdvertisingDto>;

export const getAdvertisingPageKey = (listingAliases: string[]) =>
  [SINGLE_FAMILY_ADVERTISING_PAGE_PATH, { listingAliases }] as const;

export type AdvertisingPageKey = ReturnType<typeof getAdvertisingPageKey>;

export const isAdvertisingPageKey = (queryKey: unknown): queryKey is AdvertisingPageKey =>
  Array.isArray(queryKey) &&
  queryKey[0] === SINGLE_FAMILY_ADVERTISING_PAGE_PATH &&
  Array.isArray((queryKey[1] as { listingAliases?: unknown } | undefined)?.listingAliases);

// Cut per list page, so a change to one list page (a row removed, a short page) leaves the others' reads alone.
export const chunkAdvertisingPages = (listingAliasPages: string[][]) =>
  listingAliasPages.flatMap((listingAliases) => {
    const pages: string[][] = [];
    for (let start = 0; start < listingAliases.length; start += ADVERTISING_PAGE_SIZE) {
      pages.push(listingAliases.slice(start, start + ADVERTISING_PAGE_SIZE));
    }
    return pages;
  });

// A read reflects the server when it started, so writes to a row are ordered by that start, not by arrival: when reads
// overlap, the one that started later wins even if it returns first or last. Optimistic writes take a sequence when
// they are made. Kept per query instance, so a recreated row query starts clean.
let lastRowWriteSequence = 0;
const rowWriteSequenceByQuery = new WeakMap<object, number>();

export const takeRowWriteSequence = () => {
  lastRowWriteSequence += 1;
  return lastRowWriteSequence;
};

const findRowQuery = (queryClient: QueryClient, queryKey: QueryKey) =>
  queryClient.getQueryCache().find({ queryKey, exact: true });

// Returns whether the sequence is now the row's newest write; a missing row query is never claimed.
export const claimRowWrite = (queryClient: QueryClient, queryKey: QueryKey, sequence: number) => {
  const rowQuery = findRowQuery(queryClient, queryKey);
  if (!rowQuery || (rowWriteSequenceByQuery.get(rowQuery) ?? 0) >= sequence) {
    return false;
  }
  rowWriteSequenceByQuery.set(rowQuery, sequence);
  return true;
};

export const writeRowIfNewest = <T>(queryClient: QueryClient, queryKey: QueryKey, data: T, sequence: number) => {
  if (claimRowWrite(queryClient, queryKey, sequence)) {
    queryClient.setQueryData(queryKey, data);
  }
};

// A row's own read resolves before React Query stores it, so its order is checked when the value is stored: this is
// the query's structuralSharing, which React Query calls on every write to the row. Values without a read sequence
// (page and optimistic writes, which claimed their order before writing) pass through.
const rowReadSequenceByValue = new WeakMap<object, number>();

export const markRowRead = <T extends object>(value: T, sequence: number) => {
  rowReadSequenceByValue.set(value, sequence);
  return value;
};

export const keepNewestRowRead =
  (queryClient: QueryClient, queryKey: QueryKey) =>
  (previous: unknown, next: unknown): unknown => {
    const sequence = typeof next === 'object' && next !== null ? rowReadSequenceByValue.get(next) : undefined;
    if (sequence === undefined) {
      return replaceEqualDeep(previous, next);
    }
    const isNewest = claimRowWrite(queryClient, queryKey, sequence);
    // Keeping nothing over a read would store an empty success, so a row with no data always takes the read.
    return isNewest || previous === undefined ? replaceEqualDeep(previous, next) : previous;
  };

const getRowKey = (listingAlias: string) =>
  useRentalManagerAccountViewZramSingleFamilyV2WebApiHandler_advertising.getKey({ listingAlias });

// Writes each listing into its own row query, so everything that reads or patches a row keeps using the row key.
// A cancelled read writes nothing, since React Query drops its result. Only rows that already have a query are
// written, since a query created here would have no way to fetch on its own.
export const fetchAdvertisingPage = async (
  queryClient: QueryClient,
  listingAliases: string[],
  { bypassCache = false, signal }: { bypassCache?: boolean; signal?: AbortSignal } = {},
) => {
  const sequence = takeRowWriteSequence();
  const advertisingByListingAlias =
    (await httpFrontEndRequest<AdvertisingPageParams, never, AdvertisingByListingAlias>({
      getParams: bypassCache ? { listingAliases, bypassCache } : { listingAliases },
      endpoint: SINGLE_FAMILY_ADVERTISING_PAGE_PATH,
      method: 'GET',
      contextPath: '/rental-manager-api',
    })) ?? {};
  if (signal?.aborted) {
    return advertisingByListingAlias;
  }
  Object.entries(advertisingByListingAlias).forEach(([listingAlias, advertising]) =>
    writeRowIfNewest(queryClient, getRowKey(listingAlias), advertising, sequence),
  );
  return advertisingByListingAlias;
};

// A row that mounts after its page resolved has no row query for the page to write into, so it starts from the page.
// Several cached pages can hold the same row after the list changes, so the most recently read one wins.
export const findLatestPageReadOfRow = (queryClient: QueryClient, listingAlias: string) =>
  queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) =>
        isAdvertisingPageKey(query.queryKey) &&
        query.queryKey[1].listingAliases.includes(listingAlias) &&
        (query.state.data as AdvertisingByListingAlias | undefined)?.[listingAlias] !== undefined,
    })
    .map((query) => ({
      advertising: (query.state.data as AdvertisingByListingAlias)[listingAlias],
      updatedAt: query.state.dataUpdatedAt,
    }))
    .sort((left, right) => right.updatedAt - left.updatedAt)[0];
